import {
  TRACKER_COLORS as C,
  MONO,
  formatDate,
  formatDays,
  classifyVariance,
  VARIANCE_STYLES,
} from "../../constants/trackerUi";
import { csvRow, downloadCsv } from "../../utils/csvExport";

/**
 * How a requirement actually ran, against the department's standard.
 *
 * Every number here already arrives with the requirement — `etaDays` is the
 * department's allowance for a stage, `daysElapsed` is what it really took —
 * so this is arithmetic on props, not a second trip to the server.
 *
 * Intake and the closing stage carry no allowance in any department (nor HR's
 * release), which makes them ungradeable rather than instantly late. They are
 * listed for the record but kept out of every total and out of the verdict —
 * otherwise a requirement whose only finished stage is intake reads as
 * "1 day of 0 allowed", which is worse than saying nothing.
 */
export default function CompletionReport({ requirement }) {
  const stages = requirement?.stages || [];
  const isDone = requirement?.state === "completed";

  const finished = stages.filter((s) => s.completedAt);
  const gradeable = finished.filter((s) => s.etaDays != null && s.daysElapsed != null);

  // Nothing has been completed yet — a report would be all zeroes and noise.
  if (!finished.length) return null;

  // Two different totals, deliberately. `actual` is measured against the
  // standard so it only counts gradeable stages; `spentDays` is every day
  // really spent, including the ungradeable ones — otherwise a requirement
  // whose only finished stage is intake reports "0 working days" while its
  // own table shows 1.
  const allowed = gradeable.reduce((n, s) => n + s.etaDays, 0);
  const actual = gradeable.reduce((n, s) => n + s.daysElapsed, 0);
  const spentDays = finished.reduce((n, s) => n + (s.daysElapsed || 0), 0);
  const variance = actual - allowed;
  const verdict = classifyVariance(actual, allowed);
  const tone = verdict ? VARIANCE_STYLES[verdict] : null;

  // The whole pipeline's allowance, so live progress reads against the finish
  // line rather than only against what's been done so far.
  const pipelineAllowance = stages.reduce((n, s) => n + (s.etaDays || 0), 0);
  const remainingStages = stages.length - finished.length;

  const headlineFigure = allowed ? actual : spentDays;
  const headlineUnit = allowed
    ? `of ${allowed} allowed working day${allowed === 1 ? "" : "s"}`
    : `working day${headlineFigure === 1 ? "" : "s"} — no standard set`;

  return (
    <div
      style={{
        background: C.surface,
        border: `1px solid ${C.line}`,
        borderRadius: 12,
        overflow: "hidden",
        marginBottom: 14,
      }}
    >
      <div style={{ padding: "12px 16px 14px" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}>
          <div style={MONO_CAPTION}>{isDone ? "Completion report" : "Progress so far"}</div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              const csv = buildReportCsv({
                requirement,
                finished,
                allowed,
                actual,
                spentDays,
                variance,
                verdict,
              });
              const slug = String(requirement?.title || `requirement-${requirement?.id ?? ""}`)
                .toLowerCase()
                .replace(/[^a-z0-9]+/g, "-")
                .replace(/^-|-$/g, "")
                .slice(0, 60);
              downloadCsv(`${slug || "requirement"}-report.csv`, csv);
            }}
            style={{
              fontFamily: MONO,
              fontSize: "0.6rem",
              textTransform: "uppercase",
              letterSpacing: "0.05em",
              color: C.inkSoft,
              background: C.surface,
              border: `1px solid ${C.line}`,
              borderRadius: 6,
              padding: "3px 8px",
              cursor: "pointer",
              marginBottom: 8,
              flexShrink: 0,
            }}
          >
            ↓ CSV
          </button>
        </div>

        {/* Headline: the same figure idiom as the day meter, so the two read
            as one family rather than two different dialects. */}
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12, flexWrap: "wrap" }}>
          <div style={{ display: "flex", alignItems: "baseline", gap: 6 }}>
            <span
              style={{
                fontFamily: MONO,
                fontVariantNumeric: "tabular-nums",
                fontSize: "1.34rem",
                fontWeight: 700,
                letterSpacing: "-0.02em",
                color: verdict === "over" ? C.warn : C.accent,
              }}
            >
              {headlineFigure}
            </span>
            <span style={{ fontSize: "0.8rem", color: C.inkFaint }}>{headlineUnit}</span>
          </div>

          {tone ? (
            <span
              style={{
                fontSize: "0.68rem",
                fontWeight: 800,
                padding: "2px 9px",
                borderRadius: 100,
                background: tone.bg,
                color: tone.color,
                whiteSpace: "nowrap",
              }}
            >
              {tone.label}
              {variance !== 0 ? ` · ${variance > 0 ? "+" : "−"}${Math.abs(variance)}d` : ""}
            </span>
          ) : null}
        </div>

        {/* One plain sentence, so the numbers above don't have to be read. */}
        <div style={{ fontSize: "0.78rem", color: C.inkSoft, marginTop: 8, maxWidth: "74ch" }}>
          {summarise({
            isDone,
            actual,
            spentDays,
            allowed,
            variance,
            verdict,
            finished,
            totalStages: stages.length,
            remainingStages,
            pipelineAllowance,
          })}
        </div>
      </div>

      {/* Stage-by-stage. Same row rhythm as the activity log below it. */}
      <div style={{ borderTop: `1px solid ${C.lineSoft}`, padding: "10px 16px 14px" }}>
        <div style={{ ...MONO_CAPTION, marginBottom: 4 }}>Stage by stage</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.78rem" }}>
          <thead>
            <tr style={{ color: C.inkFaint, fontFamily: MONO, fontSize: "0.6rem", textTransform: "uppercase" }}>
              <th style={{ ...th, textAlign: "left" }}>Stage</th>
              <th style={{ ...th, textAlign: "left" }}>Owner</th>
              <th style={th}>Allowed</th>
              <th style={th}>Actual</th>
              <th style={th}>Variance</th>
            </tr>
          </thead>
          <tbody>
            {finished.map((s) => {
              const v = classifyVariance(s.daysElapsed, s.etaDays);
              const vs = v ? VARIANCE_STYLES[v] : null;
              const diff = s.etaDays != null && s.daysElapsed != null ? s.daysElapsed - s.etaDays : null;
              return (
                <tr key={s.stageCode} style={{ borderTop: `1px solid ${C.lineSoft}` }}>
                  <td style={{ ...td, textAlign: "left", color: C.ink, fontWeight: 600 }}>{s.label}</td>
                  <td style={{ ...td, textAlign: "left", color: C.inkFaint }}>{s.ownerName || "—"}</td>
                  <td style={{ ...td, color: C.inkFaint }}>{s.etaDays != null ? `${s.etaDays}d` : "—"}</td>
                  <td style={{ ...td, color: C.inkSoft }}>{s.daysElapsed != null ? `${s.daysElapsed}d` : "—"}</td>
                  <td style={td}>
                    {vs ? (
                      <span style={{ color: vs.color, fontWeight: 700 }}>
                        {diff > 0 ? "+" : diff < 0 ? "−" : "±"}
                        {Math.abs(diff)}d
                      </span>
                    ) : (
                      <span style={{ color: C.inkFaint }} title="No standard set for this stage">
                        no standard
                      </span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>

        {/* Dates bookending the run, for anyone auditing later. */}
        <div style={{ display: "flex", gap: 18, flexWrap: "wrap", marginTop: 10, fontSize: "0.72rem", color: C.inkFaint, fontFamily: MONO }}>
          {requirement.createdAt ? <span>Raised {formatDate(requirement.createdAt)}</span> : null}
          {requirement.closedAt ? <span>Closed {formatDate(requirement.closedAt)}</span> : null}
          {requirement.targetDate ? <span>Target {formatDate(requirement.targetDate)}</span> : null}
        </div>
      </div>
    </div>
  );
}

/** The same figures the panel shows, as a spreadsheet-ready file. */
function buildReportCsv({ requirement, finished, allowed, actual, spentDays, variance, verdict }) {
  const lines = [
    csvRow(["Requirement", requirement?.title || `#${requirement?.id ?? ""}`]),
    csvRow(["Status", requirement?.state || ""]),
    csvRow(["Raised", requirement?.createdAt ? formatDate(requirement.createdAt) : ""]),
    csvRow(["Target", requirement?.targetDate ? formatDate(requirement.targetDate) : ""]),
    csvRow(["Closed", requirement?.closedAt ? formatDate(requirement.closedAt) : ""]),
    "",
    csvRow(["Allowed working days", allowed]),
    csvRow(["Actual working days (gradeable stages)", actual]),
    csvRow(["Total working days spent (all finished stages)", spentDays]),
    csvRow(["Variance", variance]),
    csvRow(["Verdict", verdict || "no standard set"]),
    "",
    csvRow(["Stage", "Owner", "Allowed (d)", "Actual (d)", "Variance (d)", "Completed"]),
    ...finished.map((s) =>
      csvRow([
        s.label,
        s.ownerName || "",
        s.etaDays ?? "",
        s.daysElapsed ?? "",
        s.etaDays != null && s.daysElapsed != null ? s.daysElapsed - s.etaDays : "",
        s.completedAt ? formatDate(s.completedAt) : "",
      ])
    ),
  ];
  return lines.join("\r\n");
}

/** The report in one sentence — the part most people will actually read. */
function summarise({
  isDone,
  actual,
  spentDays,
  allowed,
  variance,
  verdict,
  finished,
  totalStages,
  remainingStages,
  pipelineAllowance,
}) {
  if (!allowed) {
    // Only ungradeable stages are done, so there's nothing to grade against —
    // report the real time spent rather than a misleading zero.
    return `${finished.length} stage${finished.length === 1 ? "" : "s"} completed in ${formatDays(spentDays)}. No standard timeline applies to ${
      finished.length === 1 ? "it" : "them"
    } yet.`;
  }

  const against = `against a ${allowed}-day standard`;
  const verdictText =
    verdict === "ahead"
      ? `${formatDays(variance)} ahead`
      : verdict === "over"
      ? `${formatDays(variance)} over`
      : "on time";

  if (isDone) {
    return `Closed in ${formatDays(actual)} ${against} — ${verdictText}.`;
  }

  return `${finished.length} of ${totalStages} stages done in ${formatDays(actual)} ${against} — ${verdictText}. ${remainingStages} stage${
    remainingStages === 1 ? "" : "s"
  } left of a ${pipelineAllowance}-day pipeline.`;
}

const MONO_CAPTION = {
  fontFamily: MONO,
  fontSize: "0.6rem",
  textTransform: "uppercase",
  letterSpacing: "0.05em",
  color: C.inkFaint,
  marginBottom: 8,
};

const th = { padding: "5px 6px", textAlign: "right", fontWeight: 700 };
const td = { padding: "7px 6px", textAlign: "right", fontFamily: MONO, fontVariantNumeric: "tabular-nums" };
